import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Modal from '../../../../components/common/Modal';
import ChartProgress from '../../../../components/common/ChartProgress';
import theme from '../../../../utils/theme';
import { countGridBars } from '../../../../utils/chordBars';
import { getPartGrid } from '../../../../utils/partCharts';
import { getBeatsPerBar } from '../../../../utils/songTiming';
import { stopScroll } from '../../../../utils/autoScrollHelpers';
import { findPartNode, scrollToPartNode } from '../../../../utils/partScroll';
import { useGlobalContext } from '../../../../context/GlobalContext';

import { ReactComponent as PlusIcon } from '../../../../icons/plus.svg';

import './SongChartsModal.scss';

/**
 * The parts of this song that carry a chord chart, as a way of getting to one.
 *
 * A chart kept beside a part's words doesn't announce itself on the page — you find it by tapping
 * the part chip — so a song's charts are easy to miss entirely. This is the index of them: which
 * parts have one, how long it runs, and a tap to put that part on screen.
 */
const SongChartsModal = ({ isOpen, onClose, parts, coverage, partNumbers, timeSignature, onEditPart }) => {
  const { t } = useTranslation();
  const { scrollTimerRef, setIsScrolling } = useGlobalContext();

  const handleSelect = (partId) => {
    // Jumping mid-song leaves auto scroll pacing from where it thought it was, so it stops here
    stopScroll(scrollTimerRef, setIsScrolling);
    onClose();

    // After the dialog has gone: it locks the page while it is open, and the part's position is
    // read off the page it unlocks
    setTimeout(() => {
      const node = findPartNode(partId);

      if (node) scrollToPartNode(node);
    }, 0);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('view.chordCharts')} className="SongChartsModal">
      {/* Whether the song can be played through from top to bottom, or which parts would be skipped */}
      {/* Only while parts are missing a chart — a finished song has nothing to report */}
      {coverage?.missing.length > 0 && (
        <div className="coverage">
          <div className="summary">
            <ChartProgress done={coverage.total - coverage.missing.length} total={coverage.total} />
          </div>

          {/* A way to go and write them where the reader can, and only a list of them where not */}
          <div className="missing">
            {coverage.missing.map((part) => {
              const chip = (
                <span className={theme.renderPartColor(part.name, 'chip')}>
                  {t(`parts.${part.name}`, { defaultValue: part.name })} {partNumbers[part.id]}
                </span>
              );

              return onEditPart ? (
                <button
                  key={part.id}
                  type="button"
                  className="missing-part"
                  title={t('view.addChart')}
                  onClick={() => onEditPart(part)}
                >
                  {chip}
                  <PlusIcon className="add-icon" aria-hidden="true" />
                </button>
              ) : (
                <span key={part.id} className="missing-part">
                  {chip}
                </span>
              );
            })}
          </div>
        </div>
      )}

      <ul className="parts">
        {parts.map((part) => {
          const grid = getPartGrid(part, getBeatsPerBar(timeSignature));
          const bars = grid ? countGridBars(grid.lines, grid.sections) : 0;

          return (
            <li key={part.id}>
              <button type="button" className="part" onClick={() => handleSelect(part.id)}>
                <span className={theme.renderPartColor(part.name, 'chip')}>
                  {t(`parts.${part.name}`, { defaultValue: part.name })} {partNumbers[part.id]}
                </span>
                {bars > 0 && <span className="bars">{t('view.bars', { count: bars })}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
};

SongChartsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** The charted parts, in reading order — see getChartedParts */
  parts: PropTypes.array.isRequired,
  /** From getChartCoverage: how many parts can carry a chart, and which of them don't */
  coverage: PropTypes.shape({
    total: PropTypes.number,
    missing: PropTypes.array,
  }),
  /** Opens a part in the song form, for one missing a chart. Only given to those who can edit */
  onEditPart: PropTypes.func,
  /** Part id to the number shown on its chip, so a row reads as the page does */
  partNumbers: PropTypes.object.isRequired,
  /** The song's, which the charts' bars are counted in */
  timeSignature: PropTypes.string,
};

export default SongChartsModal;
